import {emailRecipientLimit} from "../../pricing/planEntitlements";
import type {PaidPlanId} from "../../pricing/planCatalogue";
import {nextUpgradePlan, recipientLimit, type Plan} from "../gating";

/**
 * The inline offer beside a FULL recipients field (kitecorp/transferify-ui#2524): which tier it sells
 * and the recipient count its revealed line quotes.
 *
 * Pure domain, no React (epic #26). It restates no cap: the sender's own limit comes from the same
 * `recipientLimit` the composer measures the list against, so the button can never appear while the
 * field still accepts an address — and the figure it advertises comes from the catalogue, so it can
 * never promise a list the api would refuse.
 */
export interface RecipientUpgradeNudge {
	/** The tier the button opens the upgrade dialog on. */
	target: PaidPlanId;
	/** Recipients per transfer that tier carries — the number the revealed copy quotes. */
	recipients: number;
}

/**
 * The offer for a recipients field that is full, or null when there is nothing to sell.
 *
 * <b>`>=`, not `>`.</b> The chip field goes read-only the moment the list reaches the cap, so a typed
 * address can never push the count PAST it — measuring "over" would arm this on a state the keyboard
 * cannot reach. Past the cap is still admitted, because a group whose members overrun the list gets
 * there, and it is the same fact: the sender wanted more people than their tier carries.
 *
 * Premium answers null without a tier check of its own: it is terminal, so `nextUpgradePlan` has
 * nothing to offer it. Keeping the decision there means a fifth tier would be sold automatically
 * rather than needing this rule edited.
 */
export function recipientUpgradeNudge(plan: Plan, count: number): RecipientUpgradeNudge | null {
	if (count < recipientLimit(plan)) {
		return null;
	}
	const target = nextUpgradePlan(plan);
	return target === null ? null : {target, recipients: emailRecipientLimit(target)};
}
